/**
 * Flight Popup Helper Utilities
 * 
 * This file contains utility functions extracted from FlightPopup component
 * to separate presentation logic from business logic.
 */

/**
 * Format connection time in hours and minutes
 * @param mct - Minimum connection time in minutes (can be negative for issues)
 * @returns Formatted connection time string
 */
export const formatConnectionTime = (mct: number | null | undefined): string => {
    if (mct === null || mct === undefined) return "";

    if (mct < 0) {
        // Negative connection time indicates a missed connection or connection issue
        const absoluteMinutes = Math.abs(mct);
        const hours = Math.floor(absoluteMinutes / 60);
        const minutes = absoluteMinutes % 60;
        return `Connection Issue -${hours}H ${minutes}MINS`;
    }

    if (mct === 0) return "Connection 0MINS";

    const hours = Math.floor(mct / 60);
    const minutes = mct % 60;
    return `Connection ${hours}H ${minutes}MINS`;
};

/**
 * Format status message for display using original status
 * @param flight - Flight object with status information
 * @param FlightStatusMapper - Status mapper utility
 * @returns Formatted status message
 */
export const formatStatusMessage = (flight: any, FlightStatusMapper: any): string => {
    if (!flight.status) return "UNKNOWN";

    // If status was overridden by issues, don't try to extract time from original message
    if (flight.hasStatusOverride) {
        const mappedStatus = FlightStatusMapper.mapStatus(flight.status, flight.statusIndicator);
        return mappedStatus.label;
    }

    // If no override, try to extract time from original status message
    const statusToUse = flight.originalStatusMessage || flight.status;
    const mappedStatus = FlightStatusMapper.mapStatus(statusToUse, flight.statusIndicator);

    // The mapper already includes time in the label if needed
    // Status box is ~168px wide with 10px font, so we allow up to 25 characters for delay messages
    return mappedStatus.label;
};

/**
 * Check if current flight has hop issues
 * @param transformedTrip - Transformed trip data
 * @returns Boolean indicating if current flight has hop issues
 */
export const hasCurrentFlightHopIssues = (transformedTrip: any | null): boolean => {
    if (!transformedTrip) return false;
    return false;
};

/**
 * Determine if a flight should be grayed out for airport passengers
 * @param transformedTrip - Transformed trip data
 * @param flightIndex - Index of the flight to check
 * @param infoType - Type of popup ('airport' or 'flight')
 * @returns Boolean indicating if flight should be grayed out
 */
export const shouldGrayOutFlightForAirport = (
    transformedTrip: any | null,
    flightIndex: number,
    infoType: 'flight' | 'airport'
): boolean => {
    if (!transformedTrip?.flights) return false;

    const currentFlightIndex = transformedTrip.flights.findIndex((f: any) => f.isCurrentFlight);
    const flight = transformedTrip.flights[flightIndex];

    // Gray out previous completed flights (same as for flight popups)
    if (currentFlightIndex !== -1 && flightIndex < currentFlightIndex) {
        return true;
    }

    // For airports: also gray out the current flight if it has connection details
    // This is because we highlight the connection container instead of the hop
    if (infoType === 'airport' && flightIndex === currentFlightIndex && flight?.connection) {
        return true;
    }

    return false;
};

/**
 * Check if actual time is later than scheduled time with delay greater than 15 minutes
 * Uses ISO datetime strings for accurate delay calculation across midnight and multiple days
 * @param scheduledDateTimeISO - Scheduled datetime ISO string (e.g., "2025-10-19T06:59:00")
 * @param actualDateTimeISO - Actual datetime ISO string (e.g., "2025-10-19T07:20:00")
 * @returns Boolean indicating if time is delayed by more than 15 minutes
 */
export const isTimeDelayed = (
    scheduledDateTimeISO: string | null | undefined,
    actualDateTimeISO: string | null | undefined
): boolean => {
    if (!scheduledDateTimeISO || !actualDateTimeISO) return false;

    try {
        // Parse ISO datetime strings - works with both local and UTC formats
        const scheduled = new Date(scheduledDateTimeISO);
        const actual = new Date(actualDateTimeISO);

        // Validate dates
        if (isNaN(scheduled.getTime()) || isNaN(actual.getTime())) {
            return false;
        }

        // Calculate delay in milliseconds and convert to minutes
        const delayMinutes = (actual.getTime() - scheduled.getTime()) / (1000 * 60);

        // Return true only if delay is greater than 15 minutes
        return delayMinutes > 15;
    } catch (error) {
        return false;
    }
};

/**
 * Parse time string and split into time and period components
 * @param timeStr - Time string to parse (e.g., "10:30 AM")
 * @returns Object with time and period, or null values
 */
export const parseTimeString = (timeStr: string | null | undefined): { time: string; period: string } | null => {
    if (!timeStr || timeStr === "-") return null;

    const parts = timeStr.split(' ');
    const time = parts[0];
    const period = parts[1];

    return time && period ? { time, period } : null;
};

/**
 * Calculate responsive font size based on text length
 * @param textLength - Length of the text to display
 * @returns Font size string (e.g., "10px")
 */
export const getResponsiveFontSize = (textLength: number): string => {
    // Base font size is 10px for normal text (up to 15 chars)
    // Reduce font size progressively for longer text
    if (textLength <= 15) {
        return "10px";
    } else if (textLength <= 20) {
        return "9px";
    } else if (textLength <= 25) {
        return "8px";
    } else {
        return "7px"; // Very long text
    }
};

/**
 * Calculate detail popup position with boundary constraints
 * @param params - Positioning parameters
 * @returns Position object with left and top coordinates
 */
export const calculateDetailPopupPosition = (params: {
    selectedPassenger: any | null;
    hoverPosition: { x: number; y: number } | null;
    isPopupOnLeft: boolean;
    isExpanded: boolean;
    mapContainerRect: DOMRect;
}): { left: number; top: number } | null => {
    const { selectedPassenger, hoverPosition, isPopupOnLeft, isExpanded, mapContainerRect } = params;

    if (!selectedPassenger || !hoverPosition) return null;

    const DETAIL_POPUP_WIDTH = 405;
    const DETAIL_POPUP_HEIGHT = 506;
    const POPUP_SPACING = 5;
    const HOVER_POPUP_WIDTH = 220;

    // Get boundaries based on expanded mode
    const topBoundary = isExpanded ? 0 : mapContainerRect.top;
    const bottomBoundary = isExpanded ? window.innerHeight : mapContainerRect.bottom;
    const leftBoundary = isExpanded ? 0 : mapContainerRect.left;
    const rightBoundary = isExpanded ? window.innerWidth : mapContainerRect.right;

    // Calculate initial position based on hover popup location
    let left = isPopupOnLeft
        ? hoverPosition.x - DETAIL_POPUP_WIDTH - POPUP_SPACING
        : hoverPosition.x + HOVER_POPUP_WIDTH + POPUP_SPACING;

    let top = hoverPosition.y - 170; // Adjusted offset to center with hover popup

    // Constrain to viewport boundaries
    // Check right boundary
    if (left + DETAIL_POPUP_WIDTH > rightBoundary) {
        left = rightBoundary - DETAIL_POPUP_WIDTH;
    }

    // Check left boundary
    if (left < leftBoundary) {
        left = leftBoundary;
    }

    // Check top boundary
    if (top < topBoundary) {
        top = topBoundary;
    }

    // Check bottom boundary
    if (top + DETAIL_POPUP_HEIGHT > bottomBoundary) {
        top = bottomBoundary - DETAIL_POPUP_HEIGHT;
    }

    return { left, top };
};

/**
 * Get flight status style based on status mapping color
 * @param statusColor - Color from status mapping ('Red', 'Gray', or default)
 * @returns Style object for status badge
 */
export const getFlightStatusStyle = (statusColor: string): React.CSSProperties => {
    if (statusColor === 'Red') {
        return {
            color: "#FFFFFF",
            border: "1px solid #FF4C4C",
            backgroundColor: "#FF4C4C",
        };
    } else if (statusColor === 'Gray') {
        return {
            color: "#8998AB",
            border: "1px solid #8998AB",
            backgroundColor: "transparent",
        };
    } else {
        return {
            color: "#55FC7F",
            border: "1px solid #84FFA3",
            backgroundColor: "transparent",
        };
    }
};

/**
 * Determine if flight is completed based on current flight index
 * @param transformedTrip - Transformed trip data
 * @param flightIndex - Index of flight to check
 * @param infoType - Type of popup ('airport' or 'flight')
 * @returns Boolean indicating if flight is completed
 */
export const isFlightCompleted = (
    transformedTrip: any | null,
    flightIndex: number,
    infoType: 'flight' | 'airport'
): boolean => {
    if (!transformedTrip?.flights) return false;

    if (infoType === 'airport') {
        return shouldGrayOutFlightForAirport(transformedTrip, flightIndex, infoType);
    } else {
        const currentFlightIndex = transformedTrip.flights.findIndex((f: any) => f.isCurrentFlight);
        return currentFlightIndex !== -1 && flightIndex < currentFlightIndex;
    }
};

/**
 * Get flight circle border style based on completion and current status
 * @param isCompleted - Whether flight is completed
 * @param isCurrentFlight - Whether this is the current flight
 * @returns Border style string
 */
export const getFlightCircleBorder = (isCompleted: boolean, isCurrentFlight: boolean): string => {
    if (isCompleted) {
        return "2px solid #4b5f7a"; // Disabled/grayed out for completed flights
    } else if (isCurrentFlight) {
        return "2px solid #386DFF"; // Blue border for current flight
    } else {
        return "2px solid #9CA3AF"; // Gray border for future flights
    }
};

/**
 * Get flight card border style based on completion and current status
 * @param isCompleted - Whether flight is completed
 * @param isCurrentFlight - Whether this is the current flight
 * @param infoType - Type of popup ('airport' or 'flight')
 * @returns Border style string
 */
export const getFlightCardBorder = (
    isCompleted: boolean,
    isCurrentFlight: boolean,
    infoType: 'flight' | 'airport'
): string => {
    if (isCompleted) {
        return "1.5px solid #6B7280"; // Disabled/grayed out for completed flights
    } else if (isCurrentFlight && infoType !== 'airport') {
        // Only highlight current flight for non-airport popups
        // For airports with connections, the current flight is grayed out and connection is highlighted
        return "1.5px solid #386DFF"; // Blue for current flight
    } else {
        return "1.5px solid #4B5F7A"; // Gray for normal future flight
    }
};

/**
 * Check if passenger is waiting for connection at airport
 * @param params - Parameters for connection check
 * @returns Boolean indicating if passenger is waiting
 */
export const isPassengerWaitingForConnection = (params: {
    infoType: 'flight' | 'airport';
    selectedPassenger: any | null;
    flight: any;
    nextFlight: any;
}): boolean => {
    const { infoType, selectedPassenger, flight, nextFlight } = params;

    return (
        infoType === 'airport' &&
        selectedPassenger &&
        selectedPassenger.pfhId &&
        flight.flightHistoryId &&
        nextFlight && // Must have a next flight to wait for
        String(selectedPassenger.pfhId) === String(flight.flightHistoryId)
    );
};
